import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { ResultsType } from "../calculadora-veiculo-eletrico_d";
import VeiculoEletricoTable from "./VeiculoEletricoTable";

const Tooltip = ({ children, text }: { children: React.ReactNode; text: string }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-normal z-20 text-center">
      {text}
    </div>
  </div>
);

export const VeiculoEletricoResponse = ({
  results,
}: {
  results: ResultsType;
}) => {
  return (
    <MainCard className="mt-4">
      <MainCardTitle>Análise Comparativa</MainCardTitle>

      {/* Cards de Resumo Principal - MELHORADO */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6 grid-cols-1">
        {/* Card 1: Veículo a Combustível */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          
          <Tooltip text="Custo total mensal para rodar com veículo a combustível, incluindo consumo e preço do combustível">
            <h2 className="text-base text-center font-semibold mb-4 text-gray-800 cursor-help">
              Veículo a Combustível
            </h2>
          </Tooltip>
          
          <div className="space-y-3">
            <div className="text-center pb-3 border-b border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Gasto mensal</p>
              <p className="text-2xl font-bold text-primary">
                R$ {normalSanatizerBRL(results.fuelMonthlyCost)}
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Consumo mensal</p>
              <p className="text-sm font-medium text-gray-700">
                {normalSanatizerBRL(results.fuelMonthlyConsumption)} litros
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Custo por km</p>
              <p className="text-sm font-medium text-gray-700">
                R$ {results.costPerKmFuel.toFixed(2).replace('.', ',')}
              </p>
            </div>
          </div>
        </div>

        {/* Card 2: Veículo Elétrico */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          
          <Tooltip text="Custo total mensal para rodar com veículo elétrico, incluindo consumo de energia em kWh">
            <h2 className="text-base text-center font-semibold mb-4 text-gray-800 cursor-help">
              Veículo Elétrico
            </h2>
          </Tooltip>
          
          <div className="space-y-3">
            <div className="text-center pb-3 border-b border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Gasto mensal</p>
              <p className="text-2xl font-bold text-gray-700">
                R$ {normalSanatizerBRL(results.electricMonthlyCost)}
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Consumo mensal</p>
              <p className="text-sm font-medium text-gray-700">
                {normalSanatizerBRL(results.electricMonthlyConsumption)} kWh
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Custo por km</p>
              <p className="text-sm font-medium text-gray-700">
                R$ {results.costPerKmElectric.toFixed(2).replace('.', ',')}
              </p>
            </div>
          </div>
        </div>

        {/* Card 3: Diferença */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          
          <Tooltip text="Economia mensal e anual ao optar por um veículo elétrico em relação ao combustível">
            <h2 className="text-base text-center font-semibold mb-4 text-gray-800 cursor-help">
              Diferença
            </h2>
          </Tooltip>
          
          <div className="space-y-3">
            <div className="text-center pb-3 border-b border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Economia mensal</p>
              <p className={`text-2xl font-bold ${results.monthlySavings >= 0 ? "text-primary" : "text-gray-700"}`}>
                {results.monthlySavings >= 0 ? "+" : ""}R$ {normalSanatizerBRL(Math.abs(results.monthlySavings))}
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Economia anual</p>
              <p className={`text-lg font-bold ${results.annualSavings >= 0 ? "text-primary" : "text-gray-700"}`}>
                {results.annualSavings >= 0 ? "+" : ""}R$ {normalSanatizerBRL(Math.abs(results.annualSavings))}
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Percentual</p>
              <p className={`text-base font-bold ${results.savingsPercentage >= 0 ? "text-primary" : "text-gray-700"}`}>
                {results.savingsPercentage >= 0 ? "+" : ""}{results.savingsPercentage.toFixed(1).replace('.', ',')}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Card de Resultado Destacado - MELHORADO */}
      <div className="mb-6 p-6 bg-primary rounded-lg shadow-lg text-center">
        <h2 className="text-lg text-white font-bold mb-2">
          Resultado da Comparação
        </h2>
        
        <div className="mt-4">
          <p className="text-sm text-white opacity-90 mb-2">Economia Anual</p>
          <p className={`text-2xl font-black ${results.annualSavings >= 0 ? "text-yellow-300" : "text-white"}`}>
            {results.annualSavings >= 0 ? "+" : ""}R$ {normalSanatizerBRL(Math.abs(results.annualSavings))}
          </p>
          <p className={`text-xl font-bold mt-2 ${results.annualSavings >= 0 ? "text-yellow-200" : "text-white opacity-90"}`}>
            ({results.savingsPercentage >= 0 ? "+" : ""}{results.savingsPercentage.toFixed(1).replace('.', ',')}%)
          </p>
        </div>
        
        {results.annualSavings >= 0 && (
          <p className="text-sm text-white opacity-90 mt-4">
            Economize mais de mil reais por ano com um veículo elétrico
          </p>
        )}
      </div>

      {/* Informação contextual sobre o resultado */}
      {results.monthlySavings > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-800 text-center leading-relaxed">
            <strong className="text-primary">Veículo elétrico é {results.savingsPercentage.toFixed(1).replace('.', ',')}% mais econômico</strong> que o veículo a combustível, gerando uma economia anual de <strong className="text-primary">R$ {normalSanatizerBRL(results.annualSavings)}</strong>.
          </p>
        </div>
      )}

      {results.monthlySavings < 0 && (
        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
          <p className="text-sm text-yellow-900 text-center leading-relaxed">
            Com os valores informados, o <strong>veículo a combustível</strong> está sendo <strong>{Math.abs(results.savingsPercentage).toFixed(1).replace('.', ',')}% mais econômico</strong> que o elétrico. Isso é incomum - verifique se os preços de energia e combustível estão corretos.
          </p>
        </div>
      )}

      {results.monthlySavings === 0 && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900 text-center leading-relaxed">
            Com os valores informados, ambos os veículos têm <strong>custos equivalentes</strong>.
          </p>
        </div>
      )}

      {/* Análise Qualitativa */}
      <h2 className="font-bold text-primary text-xl mb-4 text-center">
        Análise Comparativa
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-3 text-sm flex items-center">
            <span className="text-primary mr-2">✓</span> Vantagens do Veículo Elétrico
          </h4>
          <ul className="space-y-2">
            <li className="text-sm text-gray-700">• Manutenção reduzida (menos peças móveis)</li>
            <li className="text-sm text-gray-700">• Zero emissões de poluentes locais</li>
            <li className="text-sm text-gray-700">• Isenção de IPVA em alguns estados</li>
            <li className="text-sm text-gray-700">• Menor ruído e vibração</li>
          </ul>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-3 text-sm flex items-center">
            <span className="text-primary mr-2">⚠</span> Pontos de Atenção
          </h4>
          <ul className="space-y-2">
            <li className="text-sm text-gray-700">• Custo inicial do veículo mais elevado</li>
            <li className="text-sm text-gray-700">• Autonomia menor que veículos a combustível</li>
            <li className="text-sm text-gray-700">• Tempo de recarga superior ao abastecimento</li>
            <li className="text-sm text-gray-700">• Degradação da bateria ao longo do tempo</li>
          </ul>
        </div>
      </div>

      {/* Evolução Mensal */}
      <h2 className="font-bold text-primary text-xl mb-4 text-center">
        Evolução Mensal
      </h2>
      <VeiculoEletricoTable results={results} />

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
        <p className="text-sm text-yellow-900 leading-relaxed">
          <strong>Importante:</strong> Esta é uma simulação com valores estimados baseados nos dados informados. 
          Os custos reais podem variar conforme: modelo do veículo, estilo de condução, tipo de trajeto (cidade/estrada), 
          condições climáticas, tarifas de energia e tipo de recarga (residencial, pública rápida ou lenta). 
          Não foram considerados: custos de manutenção, seguro, IPVA, depreciação, custo de instalação de carregador 
          residencial e eventual substituição de bateria.
        </p>
      </div>
    </MainCard>
  );
};